import { useState } from "react";
import { Button, SectionCard } from "@/deps/ui-library";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCompanyProfile, useCreditNote, useIssueCreditNote } from "@/api/queries";
import { useCan } from "@/app/session";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { DocumentScreen, LinesTable } from "@/components/DocumentScreen";
import { DraftActions } from "@/components/DraftActions";
import { CreditNotePrint } from "@/components/print/CreditNotePrint";
import { PrintOverlay } from "@/components/print/PrintOverlay";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import { calculateTotals } from "@/lib/invoiceTotals";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { useTaxWords } from "@/app/session";

export function CreditNoteDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/sales/credit-notes/$id" });
  const { data, isPending, error } = useCreditNote(id);
  const { data: seller } = useCompanyProfile();
  const [printing, setPrinting] = useState(false);
  const issue = useIssueCreditNote();
  const can = useCan();
  const navigate = useNavigate();
  const held = useApprovalHoldsDraft("creditNote", id, data?.status === "draft");

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading credit note…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That credit note could not be loaded.</p>;

  const totals = calculateTotals(
    data.lines.map((line) => ({
      category: line.vatCategory,
      rate: line.vatRatePct,
      grossPrice: line.unitPrice,
      quantity: line.quantity,
    })),
  );

  return (
    <>
      <PrintOverlay
        open={printing}
        onClose={() => setPrinting(false)}
        title={`Credit note ${data.number ?? ""}`.trim()}
      >
        <CreditNotePrint note={data} seller={seller} />
      </PrintOverlay>
      <DocumentScreen
        title="Credit note"
        number={data.number}
        status={data.status}
        locked={data.status === "approved"}
        subtitle={`${data.customerName} · ${formatDate(data.date)}`}
        actions={
          <span className="flex items-center gap-2">
            {data.status === "draft" ? (
              <>
                {issue.isError ? (
                  <span role="alert" className="text-bad-text text-xs">
                    {issue.error instanceof Error
                      ? issue.error.message
                      : "That could not be issued."}
                  </span>
                ) : null}
                {!held && can("sales.creditnotes.create") ? (
                  <DraftActions
                    kind="creditNotes"
                    label="credit note"
                    id={id}
                    onEdit={() =>
                      void navigate({ to: "/sales/credit-notes/$id/edit", params: { id } })
                    }
                    onDeleted={() => void navigate({ to: "/sales/credit-notes" })}
                  />
                ) : null}
                <ApprovalActions
                  kind="creditNote"
                  id={id}
                  canCreate={can("sales.creditnotes.create")}
                  canIssue={can("sales.invoices.approve")}
                  issuing={issue.isPending}
                  onIssue={() => issue.mutate(id)}
                />
              </>
            ) : (
              <DownloadPdfButton kind="creditNote" id={id} number={data.number} />
            )}
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              onClick={() => setPrinting(true)}
            >
              Print
            </Button>
          </span>
        }
        facts={[
          { label: "Customer", value: data.customerName },
          { label: "Date", value: formatDate(data.date) },
          {
            label: "Credits",
            // A credit note that credits nothing in particular is legitimate — a goodwill credit —
            // but it should say so rather than leave the field blank.
            value: data.appliedTo ?? <span className="text-fg-hint">No preceding invoice</span>,
          },
          { label: "Reason", value: data.reason },
          { label: "Amount", value: <span className="num">{formatAmount(data.total)}</span> },
        ]}
        history={data.history}
        aside={
          data.detail ? (
            <SectionCard title="Note">
              <p className="text-fg text-sm">{data.detail}</p>
            </SectionCard>
          ) : null
        }
      >
        {Number(data.creditLeft ?? "0") > 0 ? (
          <SectionCard title="Held in credit">
            <p className="text-fg text-sm">
              <span className="num font-semibold">{formatAmount(data.creditLeft!)}</span> of this
              credit note is money {data.customerName} had already paid. It can be used from any of
              their open invoices.
            </p>
          </SectionCard>
        ) : null}

        <SectionCard title="Lines" flush>
          <LinesTable
            caption={`Lines of credit note ${data.number ?? "draft"}`}
            lines={data.lines.map((line) => ({
              n: line.n,
              label: line.itemName,
              account: line.accountName,
              quantity: line.quantity,
              unitPrice: formatAmount(line.unitPrice),
              tax: `${line.vatCategory} · ${line.vatRatePct}%`,
              net: formatAmount(line.lineNet),
            }))}
          />
        </SectionCard>

        <div className="grid gap-4 md:grid-cols-2">
          <SectionCard title={say("VAT breakdown")}>
            <VatBreakdownTable totals={totals} />
          </SectionCard>
          <SectionCard title="Totals">
            <TotalsBlock totals={totals} />
          </SectionCard>
        </div>
      </DocumentScreen>
    </>
  );
}
